import { fn } from '@ember/helper';
import { action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import eq from 'ember-truth-helpers/helpers/eq';
import or from 'ember-truth-helpers/helpers/or';
import type { TButtonSize } from 'volta/components/bv-btn';

import BvBtnGroup from 'volta/components/bv-btn-group';

type TBoolStr = `${boolean}`;

interface BooleanFilterArgs {
  yesLabel?: string;
  noLabel?: string;
  size?: TButtonSize;
  fullWidth?: boolean;
}

interface FiltersBooleanFilterSignature {
  Args: FilterComponentSignature<BooleanFilterArgs>['Args'] & BooleanFilterArgs;
  Element: HTMLDivElement;
}

export default class FiltersBooleanFilterComponent extends Component<FiltersBooleanFilterSignature> {
  @tracked value?: TBoolStr = this.getValueAsString();

  getValueAsString(): TBoolStr | undefined {
    const { values = [] } = this.args;
    return values.length > 0 ? (values[0]!.toString() as TBoolStr) : undefined;
  }

  @action
  valueDidChange(newValue: TBoolStr) {
    this.value = newValue === this.value ? undefined : `${newValue}`;
    this.args.onChange?.(this.value ? [this.value] : []);
  }

  @action
  setValue() {
    const val = this.getValueAsString();
    if (val !== this.value) {
      this.value = val;
    }
  }

  <template>
    <BvBtnGroup
      @outline={{true}}
      @color='primary'
      @size={{or @size @componentArgs.size}}
      @fullWidth={{or @fullWidth @componentArgs.fullWidth}}
      {{didUpdate this.setValue @values}}
      ...attributes
      as |b|
    >
      <b.btn
        @active={{eq this.value 'true'}}
        @text={{or @yesLabel @componentArgs.yesLabel (t 'yes')}}
        @onClick={{fn this.valueDidChange 'true'}}
        data-test-boolean-filter-yes={{@name}}
      />
      <b.btn
        @active={{eq this.value 'false'}}
        @text={{or @noLabel @componentArgs.noLabel (t 'no')}}
        @onClick={{fn this.valueDidChange 'false'}}
        data-test-boolean-filter-no={{@name}}
      />
    </BvBtnGroup>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::BooleanFilter': typeof FiltersBooleanFilterComponent;
    'filters/boolean-filter': typeof FiltersBooleanFilterComponent;
  }
}
